import { Link } from "react-router-dom";
import { Typography } from "@/components/ui/Typography";

interface Expense {
  id: string;
  propertyName: string;
  category: string;
  amount: number;
  date: string;
  paidBy?: string;
}

interface ExpenseListProps {
  expenses: Expense[];
  emptyMessage?: string;
}

const categoryLabels: Record<string, string> = {
  MAINTENANCE: "Maintenance",
  TAX: "Tax",
  INSURANCE: "Insurance",
  UTILITY: "Utility",
  OTHER: "Other",
};

const paidByLabels: Record<string, string> = {
  OWNER: "Paid by owner",
  TENANT: "Paid by tenant",
};

export function ExpenseList({ expenses, emptyMessage = "No recent expenses" }: ExpenseListProps) {
  if (expenses.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800/95 p-8 text-center shadow-sm dark:shadow-none">
        <p className="text-sm text-slate-500 dark:text-slate-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800/95 shadow-sm dark:shadow-none overflow-hidden">
      <div className="px-4 sm:px-5 py-4 border-b border-slate-100 dark:border-slate-700">
        <Typography variant="h3">Recent expenses</Typography>
      </div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-700">
        {expenses.map((expense) => (
          <li key={expense.id}>
            <div className="flex items-center justify-between gap-4 px-4 sm:px-5 py-3 hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                  {expense.propertyName}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {categoryLabels[expense.category] ?? expense.category}
                  {expense.paidBy && ` • ${paidByLabels[expense.paidBy] ?? expense.paidBy}`} •{" "}
                  {new Date(expense.date).toLocaleDateString("en-IN")}
                </p>
              </div>
              <p className="font-semibold text-slate-900 dark:text-slate-100 shrink-0">
                ₹{expense.amount.toLocaleString("en-IN")}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
